import { AttachInternals, Event, EventEmitter, Component, Prop, State, Watch, h } from '@stencil/core';

@Component({
  tag: 'spw-textarea',
  styleUrl: 'spw-textarea.scss',
  formAssociated: true,
  shadow: true,
})
export class SpwTextarea {
  /** Événement émis lorsque la valeur du champ textarea change */
  @Event() valueChanged: EventEmitter<string>;

  @State() internalValue: string = '';

  /** Valeur actuelle du champ textarea, mutable et reflétée dans le DOM */
  @Prop({ mutable: true, reflect: true }) value?: string;
  /** Texte à afficher au-dessus du champ textarea */
  @Prop() label?: string = '';
  /** Texte du placeholder pour le champ textarea */
  @Prop() placeholder: string;
  /** Si `true`, le champ textarea est désactivé et ne peut pas être utilisé */
  @Prop() disabled?: boolean;
  /** Attribut `name` de l'élément textarea */
  @Prop() name: string;
  /** Nombre de lignes à afficher dans le champ textarea */
  @Prop() rows?: number = 3;
  /** Longueur minimale pour la valeur du textarea */
  @Prop() minLength?: number;
  /** Longueur maximale pour la valeur du textarea */
  @Prop() maxLength?: number;
  /** Indique si l'input est un champ de recherche */
  @Prop() isSearch?: boolean = false;
  /** Indique si le champ textarea est requis */
  @Prop() required?: boolean = false;
  /** Spécifie la taille du champ textarea : 'large' ou 'medium' */
  @Prop() size?: 'large' | 'medium' = 'medium';
  /** Indicateur à afficher à droite du champ textarea */
  @Prop() indicator?: string;
  /** Texte d'assistance à afficher au-dessus du champ textarea */
  @Prop() assistiveText?: string;
  /** Message d'erreur à afficher sous le champ textarea */
  @Prop() error?: string = '';
  /** Message d'indication (hint) à afficher sous le champ textarea */
  @Prop() hint?: string = '';
  /** Message de succès à afficher sous le champ textarea */
  @Prop() success?: string = '';
  /** Affiche une icône d'erreur si vrai */
  @Prop() showErrorIcon?: boolean = true;
  /** Affiche une icône d'indication si vrai */
  @Prop() showHintIcon?: boolean = true;
  /** Affiche une icône de succès si vrai */
  @Prop() showSuccessIcon?: boolean = true;

  @AttachInternals() internals: ElementInternals;

  @Watch('value')
  valueChangedHandler(newValue: string) {
    this.internalValue = newValue;
    this.internals.setFormValue(this.internalValue);
    this.valueChanged.emit(this.internalValue);
  }

  componentWillLoad() {
    if (this.value) {
      this.internalValue = this.value;
    }
  }

  private handleChange(event) {
    this.internalValue = event.target.value;
    this.value = this.internalValue;
    this.internals.setFormValue(this.internalValue);
    this.valueChanged.emit(this.internalValue);
  }

  private renderErrorMessage() {
    if (this.error) {
      return (
        <spw-field-message variant="error" showIcon={this.showErrorIcon}>
          {this.error}
        </spw-field-message>
      );
    }
  }

  private renderHintMessage() {
    if (this.hint) {
      return (
        <spw-field-message variant="hint" showIcon={this.showHintIcon}>
          {this.hint}
        </spw-field-message>
      );
    }
  }

  private renderSuccessMessage() {
    if (this.success) {
      return (
        <spw-field-message variant="success" showIcon={this.showSuccessIcon}>
          {this.success}
        </spw-field-message>
      );
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-textarea': true,
      [`spw-textarea--${this.size}`]: true,
      'spw-textarea--is-search': this.isSearch,
      'spw-textarea--is-error': !!this.error,
      'spw-textarea--disabled': this.disabled,
    };
  }

  render() {
    return (
      <div class={this.elementClass}>
        {this.label && <spw-field-label label={this.label} name={this.name} required={this.required}></spw-field-label>}
        {this.assistiveText && (
          <spw-field-message class="spw-tw-mb-2 -spw-tw-mt-1" variant="hint" showIcon={false}>
            {this.assistiveText}
          </spw-field-message>
        )}
        <div class="spw-textarea__container">
          <textarea
            class="spw-textarea__input"
            value={this.internalValue}
            placeholder={this.placeholder}
            disabled={this.disabled}
            name={this.name}
            id={this.name}
            rows={this.rows}
            minLength={this.minLength}
            maxLength={this.maxLength}
            required={this.required}
            onInput={event => this.handleChange(event)}
          ></textarea>
        </div>
        {this.renderErrorMessage()}
        {this.renderHintMessage()}
        {this.renderSuccessMessage()}
      </div>
    );
  }
}
